/**
 * When the company is open, per weekday.
 *
 * Read by the phone's availability canvas, which draws seven day columns on one
 * shared time scale and shades the hours the company is shut. Before this there
 * was nothing to read; the schedule's own day view carries a comment saying so.
 *
 * Unset is not the same as closed. A company that never opens this page keeps a
 * working canvas at 06:00-24:00 with nothing shaded, which is why the whole
 * thing saves as null rather than as seven closed days.
 *
 * Saving is per row and immediate, like every other setting on this page. The
 * server validates the whole object and names the day that is wrong
 * (backend/services/openingHours.js), and that message is shown against the row
 * it belongs to rather than as a toast, because a toast does not say which
 * Tuesday it means.
 */
import { useState } from 'react';

import { T } from '@/i18n';
import type { OpeningHours } from '@/shell/tenant';

const DAYS: Array<{ key: keyof OpeningHours; label: string }> = [
  { key: 'mon', label: 'Mandag' },
  { key: 'tue', label: 'Tirsdag' },
  { key: 'wed', label: 'Onsdag' },
  { key: 'thu', label: 'Torsdag' },
  { key: 'fri', label: 'Fredag' },
  { key: 'sat', label: 'Lørdag' },
  { key: 'sun', label: 'Søndag' },
];

/**
 * What the phone draws when nothing is set, shown here so the page says what
 * the app will do. 23:59 rather than midnight because `<input type="time">`
 * stops at 23:59; the server accepts 24:00 from anything that sends it.
 */
const FALLBACK: [string, string] = ['06:00', '23:59'];

const minutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':');
  return Number(h) * 60 + Number(m);
};

export default function Apningstider({
  hours,
  onSave,
}: {
  hours: OpeningHours | null | undefined;
  onSave: (next: OpeningHours | null) => Promise<void>;
}) {
  const [error, setError] = useState<Partial<Record<string, string>>>({});
  const set = hours ?? null;

  const write = async (key: keyof OpeningHours, value: [string, string] | null) => {
    const next: OpeningHours = { ...(set ?? {}), [key]: value };
    // All seven closed is the same as saying nothing, and null is what the
    // clients understand as "not set", so it collapses rather than storing a
    // company that is never open.
    const anyOpen = DAYS.some((d) => next[d.key] != null);
    setError((e) => ({ ...e, [key]: undefined }));
    await onSave(anyOpen ? next : null);
  };

  const change = async (key: keyof OpeningHours, which: 0 | 1, value: string) => {
    const current = set?.[key] ?? FALLBACK;
    const pair: [string, string] = which === 0 ? [value, current[1]] : [current[0], value];
    if (minutes(pair[1]) <= minutes(pair[0])) {
      setError((e) => ({ ...e, [key]: T('Stengetid må være etter åpningstid.') }));
      return;
    }
    await write(key, pair);
  };

  return (
    <div className="opening-hours">
      <p className="hint">
        {set
          ? T('Brukes av appen til å vise når de ansatte kan si seg tilgjengelige.')
          : T('Ikke satt. Appen viser 06:00–23:59 alle dager til dette er fylt ut.')}
      </p>
      {DAYS.map(({ key, label }) => {
        const day = set?.[key];
        const closed = set != null && day === null;
        const value = day ?? FALLBACK;
        return (
          <div key={key} className="opening-hours-row">
            <label className="opening-hours-day" htmlFor={`oh-${key}-from`}>
              {T(label)}
            </label>
            {closed ? (
              <span className="hint opening-hours-closed">{T('Stengt')}</span>
            ) : (
              <>
                <input
                  id={`oh-${key}-from`}
                  className="inp"
                  type="time"
                  aria-label={`${T(label)}, ${T('åpner')}`}
                  aria-invalid={!!error[key]}
                  value={value[0]}
                  onChange={(e) => void change(key, 0, e.target.value)}
                />
                <span aria-hidden>–</span>
                <input
                  className="inp"
                  type="time"
                  aria-label={`${T(label)}, ${T('stenger')}`}
                  aria-invalid={!!error[key]}
                  value={value[1]}
                  onChange={(e) => void change(key, 1, e.target.value)}
                />
              </>
            )}
            <button
              type="button"
              className="btn sm"
              onClick={() => void write(key, closed ? FALLBACK : null)}
            >
              {closed ? T('Åpne') : T('Steng')}
            </button>
            {error[key] && (
              <span role="alert" className="opening-hours-error">
                {error[key]}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
